import React, { useMemo, useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Autocomplete,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Slider,
  TextField,
  Tooltip,
  Typography,
  createTheme,
  ThemeProvider,
  Popover,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { Add, Remove, Refresh, Search as SearchIcon, Visibility, VisibilityOff, ShowChart, TableRows, FileDownload, ExpandMore, ExpandLess } from '@mui/icons-material';
import { getMinAndMaxDate, getColumnsUniqueValues, filterProjectProductViewData, refreshProjectProductData, fetchRawData } from './api';
import { useParams, useNavigate } from "react-router-dom";
import {
  getProductViewDataProjectKey,
  getProductViewPriceExportFilename,
  getProductViewAccent,
  getLineTypeFromPriceMonitoringSegment,
  PRODUCT_VIEW_ROUTE_SEGMENT,
} from './productViewProjectConfig';
import { useAuth } from './AuthContext';

import { saveAs } from 'file-saver';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import KpiElements from './KpiElement';
import MonitoringChartLegend from './MonitoringChartLegend';

const stringToColor = (str = '') => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${Math.abs(hash) % 360}, 45%, 42%)`;
};

export const inSaleOptions = ['All', 'Yes', 'No'];
export const priceTypeOptions = ['Actual', 'Promotional', 'All'];
export const lineTypeOptions = ['Retailers', 'Brands', 'Products'];

Chart.register(...registerables, ChartDataLabels);

const chartOptions = (yAxisTitle, yAxisUnit, missingDataMode = 'dashed line', axisScaleOverrides = {}) => {
  const { min: yMinOverride, max: yMaxOverride, stepSize: yStepOverride } = axisScaleOverrides;
  const yScale = {
    title: {
      display: true,
      text: yAxisUnit ? `${yAxisTitle} (${yAxisUnit})` : yAxisTitle,
      font: {
        size: 18,
        weight: 'bold',
      },
    },
  };
  if (typeof yMinOverride === 'number' && !isNaN(yMinOverride)) {
    yScale.min = yMinOverride;
  }
  if (typeof yMaxOverride === 'number' && !isNaN(yMaxOverride)) {
    yScale.max = yMaxOverride;
  }
  if (typeof yStepOverride === 'number' && !isNaN(yStepOverride)) {
    yScale.ticks = { stepSize: yStepOverride };
  }

  return {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  stacked: false,
  scales: {
    x: {
      ticks: {
        autoSkip: true,
        maxTicksLimit: 10,
      },
    },
    y: yScale,
  },
  elements: {
    point: {
      radius: 1,
    },
    line: {
      spanGaps: missingDataMode !== 'no line',
    },
  },
  layout: {
    padding: {
      right: 80,
      top: 10,
      left: 10,
      bottom: 10,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      filter: function (tooltipItem) {
        // Exclude gap-spanning segments from tooltip
        return !tooltipItem.dataset.isGapSpanning;
      },
      itemSort: function (a, b) {
        const aValue = a.parsed.y !== null ? a.parsed.y : -Infinity;
        const bValue = b.parsed.y !== null ? b.parsed.y : -Infinity;
        return bValue - aValue;
      },
      callbacks: {
        label: function (context) {
          const label = context.dataset.label || '';
          const value = context.parsed.y;
          if (value === null || value === undefined || isNaN(value)) {
            return `${label}: No data`;
          }
          const formattedValue = value.toFixed(2);
          return yAxisUnit ? `${label}: ${formattedValue} ${yAxisUnit}` : `${label}: ${formattedValue}`;
        },
      },
    },
    datalabels: {
      align: 'right',
      color: (ctx) => ctx.dataset.borderColor,
      display: (ctx) => ctx.dataIndex === ctx.dataset.data.length - 1,
      formatter: (v, ctx) => ctx.dataset.label,
      offset: 8,
      font: {
        weight: 600,
        size: 10,
      },
    },
  },
};
};

function BatProductView() {
  const viewType = 'product_view';

  const { projectKey, groupBySegment } = useParams();
  const navigate = useNavigate();
  const { uiStyle } = useAuth();
  const lockedLineType = useMemo(() => getLineTypeFromPriceMonitoringSegment(groupBySegment), [groupBySegment]);
  const hideGroupByFilter = Boolean(groupBySegment);
  const dataProjectKey = useMemo(() => getProductViewDataProjectKey(projectKey), [projectKey]);
  const accent = useMemo(() => getProductViewAccent(projectKey, uiStyle), [projectKey, uiStyle]);
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: accent.main,
          },
        },
      }),
    [accent.main]
  );
  const [selectedBrands, setSelectedBrands] = useState([]);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [inSaleStatus, setInSaleStatus] = useState(priceTypeOptions[0]);
  const [lineType, setLineType] = useState(lineTypeOptions[0]);
  const [selectedPackSizes, setSelectedPackSizes] = useState([10]);

  useLayoutEffect(() => {
    if (lockedLineType) {
      setLineType(lockedLineType);
    }
  }, [lockedLineType]);

  useEffect(() => {
    if (!groupBySegment) return;
    if (!getLineTypeFromPriceMonitoringSegment(groupBySegment)) {
      navigate(`/${projectKey}/${PRODUCT_VIEW_ROUTE_SEGMENT}/retailers`, { replace: true });
    }
  }, [groupBySegment, projectKey, navigate]);

  const priceMonitoringChartTitle = useMemo(() => {
    const nounByLineType = { Retailers: 'retailers', Brands: 'brands', Products: 'products' };
    const noun = nounByLineType[lineType] ?? lineType.toLowerCase();
    return `Price monitoring by selected ${noun}`;
  }, [lineType]);

  const CATEGORY_GROUPS = {
    'Traditional Oral': ['White Portion', 'Portionssnus'],
    'Modern Oral': ['All White', 'Nicotine Pouches'],
  };
  const ALL_CATEGORY_VALUES = Object.values(CATEGORY_GROUPS).flat();
  const [selectedCategories, setSelectedCategories] = useState(ALL_CATEGORY_VALUES);

  const handleCategoryGroupToggle = (group) => {
    const groupCats = CATEGORY_GROUPS[group] || [];
    const allSelected = groupCats.every((c) => selectedCategories.includes(c));
    if (allSelected) {
      setSelectedCategories(selectedCategories.filter((c) => !groupCats.includes(c)));
    } else {
      setSelectedCategories([...new Set([...selectedCategories, ...groupCats])]);
    }
  };

  const categoryRenderValue = (selected) => {
    if (selected.length === 0) return 'None';
    if (selected.length === ALL_CATEGORY_VALUES.length) return 'All';
    const parts = [];
    for (const [group, cats] of Object.entries(CATEGORY_GROUPS)) {
      const sel = cats.filter((c) => selected.includes(c));
      if (sel.length === cats.length) parts.push(group);
      else parts.push(...sel);
    }
    return parts.join(', ');
  };

  const [selectedCountries, setSelectedCountries] = useState([]);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [retailerSearchTerm, setRetailerSearchTerm] = useState('');

  const [lineData, setLineData] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [topN, setTopN] = useState(30);
  const [visibleDatasets, setVisibleDatasets] = useState(new Set());
  const [legendSearchTerm, setLegendSearchTerm] = useState('');

  const [columnsUniqueValues, setColumnsUniqueValues] = useState(null);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [dateSliderValue, setDateSliderValue] = useState([0, 0]);

  const [kpiData, setKpiData] = useState(null);

  const [refreshCounter, setRefreshCounter] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDbRefreshing, setIsDbRefreshing] = useState(false);
  const activeFetchIdRef = useRef(0);

  // Full Dataset panel state
  const [showFullDataset, setShowFullDataset] = useState(false);
  const [rawData, setRawData] = useState(null);
  const [rawDataLoading, setRawDataLoading] = useState(false);
  const [rawDataPage, setRawDataPage] = useState(0);
  const [rawDataPageSize, setRawDataPageSize] = useState(100);
  const lastFilterSignatureRef = useRef(null);
  const hasInitializedRetailersRef = useRef(false);

  const handleDataRefresh = useCallback(async () => {
    setIsDbRefreshing(true);
    const startDateString = startDate ? startDate.format('YYYY-MM-DD') : null;
    const endDateString = endDate ? endDate.format('YYYY-MM-DD') : null;

    const result = await refreshProjectProductData(projectKey, startDateString, endDateString);
    if (result?.success) {
      setRefreshCounter(prev => prev + 1);
    } else {
      console.error(`Failed to refresh ${projectKey} data from database.`);
    }
    setIsDbRefreshing(false);
  }, [projectKey, startDate, endDate]);

  const handleBrandChange = (_, newBrands) => {
    setSelectedBrands(newBrands);
    setSelectedProducts([]);
  };

  const handleStartDateChange = (dateValue) => {
    setStartDate(dateValue);
  }

  const handleEndDateChange = (dateValue) => {
    setEndDate(dateValue);
  }

  const extractCountryCodeFromRetailer = (retailerName) => {
    const match = (retailerName || '').match(/\(([A-Z]{2})\)\s*$/);
    return match ? match[1] : null;
  };

  const countryToCode = {
    Sweden: 'SE',
    Norway: 'NO',
  };

  const handleCountryChange = (newCountries) => {
    const prevHasSweden = selectedCountries.includes('Sweden');
    const prevHasNorway = selectedCountries.includes('Norway');
    const newHasSweden = newCountries.includes('Sweden');
    const newHasNorway = newCountries.includes('Norway');

    if (newHasSweden && newHasNorway) {
      if (!prevHasSweden && newHasSweden) {
        newCountries = newCountries.filter(c => c !== 'Norway');
      } else if (!prevHasNorway && newHasNorway) {
        newCountries = newCountries.filter(c => c !== 'Sweden');
      } else {
        newCountries = prevHasSweden
          ? newCountries.filter(c => c !== 'Norway')
          : newCountries.filter(c => c !== 'Sweden');
      }
    }

    setSelectedCountries(newCountries);
  };

  const isCurrencyLocked = selectedCountries.includes('Sweden') || selectedCountries.includes('Norway');

  const filteredRetailers = useMemo(() => {
    const allRetailers = columnsUniqueValues?.Retailer ?? [];
    const allowedCodes = selectedCountries
      .map((c) => countryToCode[c])
      .filter(Boolean);

    if (!allowedCodes.length) return allRetailers;
    const allowed = new Set(allowedCodes);

    return allRetailers.filter((r) => {
      const code = extractCountryCodeFromRetailer(r);
      return !code || allowed.has(code);
    });
  }, [columnsUniqueValues?.Retailer, selectedCountries]);

  useEffect(() => {
    if (!columnsUniqueValues?.Retailer) return;
    setSelectedRetailers((prev) => prev.filter((r) => filteredRetailers.includes(r)));
  }, [filteredRetailers, columnsUniqueValues?.Retailer]);

  const filterSignature = useMemo(() => {
    const startDateString = startDate ? startDate.format('YYYY-MM-DD') : null;
    const endDateString = endDate ? endDate.format('YYYY-MM-DD') : null;

    return JSON.stringify({
      dataProjectKey,
      viewType,
      selectedCountries,
      selectedRetailers,
      selectedBrands,
      selectedProducts,
      selectedCurrency,
      inSaleStatus,
      lineType,
      selectedPackSizes,
      selectedCategories,
      startDateString,
      endDateString,
      refreshCounter,
    });
  }, [
    dataProjectKey,
    viewType,
    selectedCountries,
    selectedRetailers,
    selectedBrands,
    selectedProducts,
    selectedCurrency,
    inSaleStatus,
    lineType,
    selectedPackSizes,
    selectedCategories,
    startDate,
    endDate,
    refreshCounter,
  ]);

  useLayoutEffect(() => {
    if (!columnsUniqueValues) return;
    if (lastFilterSignatureRef.current !== filterSignature) {
      lastFilterSignatureRef.current = filterSignature;
      setIsRefreshing(true);
    }
  }, [filterSignature, columnsUniqueValues]);

  const availableDates = useMemo(() => {
    if (!minDate || !maxDate) return [];
    const dates = [];
    let current = minDate.startOf('day');
    const end = maxDate.startOf('day');
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      dates.push(current);
      current = current.add(1, 'day');
    }
    return dates;
  }, [minDate, maxDate]);

  useEffect(() => {
    if (!availableDates.length || !startDate || !endDate) return;
    const startIdx = availableDates.findIndex((d) => d.isSame(startDate, 'day'));
    const endIdx = availableDates.findIndex((d) => d.isSame(endDate, 'day'));
    setDateSliderValue([
      startIdx === -1 ? 0 : startIdx,
      endIdx === -1 ? availableDates.length - 1 : endIdx,
    ]);
  }, [availableDates, startDate, endDate]);

  const handleDateSliderChange = (_, newValue) => {
    setDateSliderValue(newValue);
  };

  const handleDateSliderCommit = (_, newValue) => {
    if (!availableDates.length) return;
    setStartDate(availableDates[newValue[0]]);
    setEndDate(availableDates[newValue[1]]);
  };

  const columnsWithClickableUrls = gridData?.columns.map((col) => {
    if (col.field.toLowerCase().includes('url')) {
      return {
        ...col,
        renderCell: (params) => (
          <a href={params.value} target="_blank" rel="noopener noreferrer">
            {params.value}
          </a>
        ),
      };
    }
    return col;
  });

  useEffect(() => {
    const fetchMetadata = async () => {
      const dateResult = await getMinAndMaxDate(dataProjectKey, viewType);
      if (dateResult) {
        const newMinDate = dayjs(dateResult.min_date);
        const newMaxDate = dayjs(dateResult.max_date);
        setMinDate(newMinDate);
        setMaxDate(newMaxDate);
        if (!startDate) {
          const oneMonthAgo = newMaxDate.subtract(30, 'day');
          setStartDate(oneMonthAgo.isBefore(newMinDate) ? newMinDate : oneMonthAgo);
        }
        if (!endDate) setEndDate(newMaxDate);
      }

      const uniqueVals = await getColumnsUniqueValues(dataProjectKey, viewType);
      if (uniqueVals) {
        if (uniqueVals.Currency?.length) setSelectedCurrency(uniqueVals.Currency[0]);
        setColumnsUniqueValues(uniqueVals);
        if (uniqueVals.Country) {
          let countries = [...uniqueVals.Country];
          if (countries.includes('Sweden') && countries.includes('Norway')) {
            countries = countries.filter(c => c !== 'Norway');
          }
          setSelectedCountries(countries);
        }
        if (uniqueVals['Units (#)']?.length) {
          const packNums = uniqueVals['Units (#)'].map(Number).filter(n => !isNaN(n) && n > 0);
          setSelectedPackSizes(packNums.includes(10) ? [10] : packNums.slice(0, 1));
        }
        if (uniqueVals.Retailer) {
          const effectiveCountries = (() => {
            if (!uniqueVals.Country) return [];
            let countries = [...uniqueVals.Country];
            if (countries.includes('Sweden') && countries.includes('Norway')) {
              countries = countries.filter(c => c !== 'Norway');
            }
            return countries;
          })();

          const allowedCodes = effectiveCountries.map((c) => countryToCode[c]).filter(Boolean);
          const availableRetailers = (() => {
            if (!allowedCodes.length) return [...uniqueVals.Retailer];
            const allowed = new Set(allowedCodes);
            return uniqueVals.Retailer.filter((r) => {
              const code = extractCountryCodeFromRetailer(r);
              return !code || allowed.has(code);
            });
          })();

          const normalizeRetailer = (r) =>
            (r || '').replace(/\s*\([A-Z]{2}\)\s*$/, '').trim().toLowerCase();

          const defaultRetailerBases = ['Snusbolaget', 'Snuset', 'Minprilla', 'Snussidan'];
          const defaultRetailers = defaultRetailerBases
            .map((base) => availableRetailers.find((r) => normalizeRetailer(r) === base.toLowerCase()))
            .filter(Boolean);

          const initialSelection = defaultRetailers.length ? defaultRetailers : availableRetailers;

          setSelectedRetailers((prev) => {
            if (hasInitializedRetailersRef.current) return prev;
            hasInitializedRetailersRef.current = true;
            return initialSelection;
          });
        }
      }
    };

    fetchMetadata();
  }, [refreshCounter, dataProjectKey]);

  useEffect(() => {
    if (!columnsUniqueValues) return;

    const fetchLineAndGridData = async () => {
      const fetchId = ++activeFetchIdRef.current;
      setIsRefreshing(true);
      const startDateString = startDate ? startDate.format('YYYY-MM-DD') : null;
      const endDateString = endDate ? endDate.format('YYYY-MM-DD') : null;

      try {
        const selectedEans = selectedProducts.map((p) => (typeof p === 'string' ? p : p.ean)).filter(Boolean);
        const data = await filterProjectProductViewData(
          dataProjectKey,
          viewType,
          selectedCountries,
          selectedRetailers,
          selectedBrands,
          null,
          selectedCurrency,
          inSaleStatus,
          startDateString,
          endDateString,
          lineType,
          selectedPackSizes,
          selectedCategories.length === ALL_CATEGORY_VALUES.length ? null : selectedCategories,
          selectedEans.length ? selectedEans : null
        );

        if (fetchId !== activeFetchIdRef.current) return;

        if (data) {
          setLineData(data.graph_data);
          setGridData(data.grid_data);
          setKpiData(data.kpi_data);
          setColumnsUniqueValues((prev) => {
            const merged = { ...prev, ...data.columns_unique_values };
            if (prev?.Country) merged.Country = prev.Country;
            if (prev?.Retailer) merged.Retailer = prev.Retailer;
            // Only overwrite ReferenceProducts if the server returned a new list.
            if (prev?.ReferenceProducts && !data.columns_unique_values?.ReferenceProducts) {
              merged.ReferenceProducts = prev.ReferenceProducts;
            }
            return merged;
          });
        } else {
          console.error('Fetching data failed. No data are being assigned.');
        }
      } finally {
        if (fetchId === activeFetchIdRef.current) {
          setIsRefreshing(false);
        }
      }
    };

    fetchLineAndGridData();
  }, [
    dataProjectKey,
    selectedCountries,
    selectedRetailers,
    selectedBrands,
    selectedProducts,
    selectedCurrency,
    inSaleStatus,
    lineType,
    selectedPackSizes,
    selectedCategories,
    startDate,
    endDate,
    refreshCounter
  ]);

  const totalDatasetsCount = useMemo(() => lineData?.datasets?.length ?? 0, [lineData]);

  const topNDatasets = useMemo(() => {
    if (!lineData || !lineData.datasets || lineData.datasets.length === 0) {
      return [];
    }
    const datasetsWithAvg = lineData.datasets.map((dataset) => {
      const values = (dataset.data || []).filter((v) => v !== null && v !== undefined && !isNaN(v));
      const avg = values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
      return { dataset, avg };
    });

    datasetsWithAvg.sort((a, b) => b.avg - a.avg);
    return datasetsWithAvg.slice(0, Math.max(1, topN)).map((item) => item.dataset);
  }, [lineData, topN]);

  const [showChart, setShowChart] = useState(true);
  const [legendExpanded, setLegendExpanded] = useState(true);
  const [axisAnchorEl, setAxisAnchorEl] = useState(null);
  const [yMin, setYMin] = useState('');
  const [yMax, setYMax] = useState('');
  const [yStepSize, setYStepSize] = useState('');
  const [showChartLabels, setShowChartLabels] = useState(true);
  const [missingDataMode, setMissingDataMode] = useState('dashed line');

  const chartTableColumns = useMemo(() => {
    if (!topNDatasets.length) return [];
    return [
      { field: 'date', headerName: 'Date', width: 110 },
      ...topNDatasets.map((ds) => ({
        field: ds.label,
        headerName: ds.label,
        width: 140,
        align: 'right',
        headerAlign: 'right',
      })),
    ];
  }, [topNDatasets]);

  const chartTableRows = useMemo(() => {
    if (!lineData?.labels || !topNDatasets.length) return [];
    return lineData.labels.map((date, i) => {
      const row = { id: i, date };
      topNDatasets.forEach((ds) => {
        const v = ds.data[i];
        row[ds.label] = (v != null && !isNaN(v)) ? Number(v).toFixed(2) : '';
      });
      return row;
    });
  }, [lineData, topNDatasets]);

  const handleExportCSV = useCallback(() => {
    if (!lineData?.labels || !topNDatasets.length) return;
    const headers = ['Date', ...topNDatasets.map((ds) => `"${ds.label}"`)];
    const dataRows = lineData.labels.map((date, i) => {
      const vals = topNDatasets.map((ds) => (ds.data[i] != null ? Number(ds.data[i]).toFixed(2) : ''));
      return [date, ...vals].join(',');
    });
    const csv = [headers.join(','), ...dataRows].join('\n');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), getProductViewPriceExportFilename(projectKey));
  }, [lineData, topNDatasets, projectKey]);

  useEffect(() => {
    if (topNDatasets.length > 0) {
      setVisibleDatasets(new Set(topNDatasets.map((ds) => ds.label)));
    }
  }, [topNDatasets]);

  useEffect(() => {
    if (!columnsUniqueValues?.Currency) return;
    const hasSweden = selectedCountries.includes('Sweden');
    const hasNorway = selectedCountries.includes('Norway');

    if (hasSweden && !hasNorway) {
      const sekCurrency = columnsUniqueValues.Currency.find(c => c.includes('SEK'));
      if (sekCurrency) setSelectedCurrency(sekCurrency);
    } else if (hasNorway && !hasSweden) {
      const nokCurrency = columnsUniqueValues.Currency.find(c => c.includes('NOK'));
      if (nokCurrency) setSelectedCurrency(nokCurrency);
    }
  }, [selectedCountries, columnsUniqueValues]);

  const chartData = useMemo(() => {
    if (!lineData || !lineData.labels) {
      return null;
    }
    let datasets = visibleDatasets.size
      ? topNDatasets.filter((ds) => visibleDatasets.has(ds.label))
      : topNDatasets;
    
    // Preprocess datasets to split at gaps and apply borderDash for dashed line mode
    if (missingDataMode === 'dashed line') {
      const processedDatasets = [];
      const isMissing = (v) => v === null || v === undefined || (typeof v === 'number' && isNaN(v));
      
      datasets.forEach((dataset) => {
        const data = dataset.data || [];
        const validIndices = [];
        
        // Find all valid data points
        for (let i = 0; i < data.length; i++) {
          if (!isMissing(data[i])) {
            validIndices.push(i);
          }
        }
        
        if (validIndices.length === 0) return;
        
        // Group consecutive indices into continuous segments
        const continuousSegments = [];
        let currentSegment = [validIndices[0]];
        
        for (let i = 1; i < validIndices.length; i++) {
          if (validIndices[i] - validIndices[i - 1] === 1) {
            // Consecutive - add to current segment
            currentSegment.push(validIndices[i]);
          } else {
            // Gap detected - save current segment and start new one
            continuousSegments.push([...currentSegment]);
            currentSegment = [validIndices[i]];
          }
        }
        continuousSegments.push(currentSegment);
        
        // Create datasets: solid for continuous segments, dashed for gap-spanning connections
        continuousSegments.forEach((segment, segIdx) => {
          // Solid segment for continuous data
          const segmentData = new Array(data.length).fill(null);
          segment.forEach(idx => {
            segmentData[idx] = data[idx];
          });
          
          processedDatasets.push({
            ...dataset,
            data: segmentData,
            borderDash: [],
            label: dataset.label, // Keep label for all solid segments so tooltip works
            pointRadius: segIdx === 0 ? 1 : 0,
            pointHoverRadius: segIdx === 0 ? 1 : 0,
          });
          
          // If there's a next segment, create dashed connection
          if (segIdx < continuousSegments.length - 1) {
            const nextSegment = continuousSegments[segIdx + 1];
            const gapData = new Array(data.length).fill(null);
            gapData[segment[segment.length - 1]] = data[segment[segment.length - 1]]; // Last point of current
            gapData[nextSegment[0]] = data[nextSegment[0]]; // First point of next
            
            processedDatasets.push({
              ...dataset,
              data: gapData,
              borderDash: [2, 2],
              label: '',
              pointRadius: 0,
              pointHoverRadius: 0,
              isGapSpanning: true, // Mark as gap-spanning segment to exclude from tooltips
            });
          }
        });
      });
      
      datasets = processedDatasets;
    }
    
    return {
      labels: lineData.labels,
      datasets,
    };
  }, [lineData, topNDatasets, visibleDatasets, missingDataMode]);

  const axisScaleOverrides = useMemo(() => {
    const min = yMin !== '' && !isNaN(Number(yMin)) ? Number(yMin) : undefined;
    const max = yMax !== '' && !isNaN(Number(yMax)) ? Number(yMax) : undefined;
    const stepSize = yStepSize !== '' && !isNaN(Number(yStepSize)) ? Number(yStepSize) : undefined;
    return { min, max, stepSize };
  }, [yMin, yMax, yStepSize]);

  const lineChartOptions = useMemo(() => {
    const base = chartOptions('Price', selectedCurrency, missingDataMode, axisScaleOverrides);
    return {
      ...base,
      plugins: {
        ...base.plugins,
        datalabels: {
          ...base.plugins.datalabels,
          display: (ctx) =>
            showChartLabels &&
            Boolean(ctx.dataset.label) &&
            ctx.dataIndex === ctx.dataset.data.length - 1,
        },
      },
    };
  }, [selectedCurrency, missingDataMode, axisScaleOverrides, showChartLabels]);

  const axisSettingsActive =
    yMin !== '' || yMax !== '' || yStepSize !== '' || !showChartLabels;

  const handleToggleDataset = (label) => {
    setVisibleDatasets((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const handleShowAll = () => {
    setVisibleDatasets(new Set(topNDatasets.map((ds) => ds.label)));
  };

  const handleHideAll = () => {
    setVisibleDatasets(new Set());
  };

  const loadFullDataset = useCallback(async (page = 0, pageSize = rawDataPageSize) => {
    setRawDataLoading(true);
    const startDateString = startDate ? startDate.format('YYYY-MM-DD') : null;
    const endDateString = endDate ? endDate.format('YYYY-MM-DD') : null;
    const selectedEans = selectedProducts.map((p) => (typeof p === 'string' ? p : p.ean)).filter(Boolean);
    const result = await fetchRawData({
      projectKey: dataProjectKey,
      viewType,
      countries: selectedCountries,
      retailers: selectedRetailers,
      brands: selectedBrands.length ? selectedBrands : null,
      referenceEans: selectedEans.length ? selectedEans : null,
      currency: selectedCurrency,
      inSaleStatus,
      startDateString,
      endDateString,
      packSizes: selectedPackSizes,
      categories: selectedCategories.length === ALL_CATEGORY_VALUES.length ? null : selectedCategories,
      page,
      pageSize,
    });
    setRawData(result);
    setRawDataLoading(false);
  }, [dataProjectKey, viewType, selectedCountries, selectedRetailers, selectedBrands, selectedProducts,
      selectedCurrency, inSaleStatus, startDate, endDate, selectedPackSizes, selectedCategories,
      ALL_CATEGORY_VALUES.length, rawDataPageSize]);

  const handleToggleFullDataset = () => {
    if (!showFullDataset && !rawData) {
      loadFullDataset(0, rawDataPageSize);
    }
    setShowFullDataset(prev => !prev);
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      marginInline: 'auto',
      padding: '0 20px',
      overflowX: 'auto'
    }}>
      <div>
      <ThemeProvider theme={theme}>
      <Paper
        elevation={0}
        sx={{
          p: 1.25,
          mb: 2,
          mt: 1,
          backgroundColor: '#f5f5f5',
          border: '1px solid #e0e0e0',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: hideGroupByFilter
              ? 'minmax(0, 1fr) minmax(0, 1fr) auto minmax(0, 1fr) auto'
              : 'minmax(0, 1fr) minmax(0, 1fr) auto auto minmax(0, 1fr) auto',
            gap: 1.25,
            alignItems: 'start',
          }}
        >
          {/* Column 1: Countries + Retailers */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, overflow: 'hidden' }}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                '& .MuiInputBase-input': { padding: '4px 8px' },
                '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
                '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
              }}
            >
              <InputLabel id="pt-country-select-label">Countries</InputLabel>
              <Select
                labelId="pt-country-select-label"
                id="pt-country-select"
                value={selectedCountries}
                label="Countries"
                multiple
                onChange={(e) => handleCountryChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                input={<OutlinedInput label="Countries" />}
                renderValue={(selected) => selected.join(', ')}
                sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 400, width: 280 } },
                  autoFocus: false,
                }}
                onClose={() => setCountrySearchTerm('')}
              >
                <ListSubheader>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Search countries..."
                    fullWidth
                    value={countrySearchTerm}
                    onChange={(e) => setCountrySearchTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </ListSubheader>
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const allCountries = columnsUniqueValues?.Country ?? [];
                      if (selectedCountries.length === allCountries.length) {
                        handleCountryChange([]);
                      } else {
                        handleCountryChange([...allCountries]);
                      }
                    }}
                  >
                    Select all
                  </Button>
                </Box>
                {(columnsUniqueValues?.Country ?? [])
                  .filter((c) => c.toLowerCase().includes(countrySearchTerm.toLowerCase()))
                  .map((country) => (
                    <MenuItem
                      key={country}
                      value={country}
                      dense
                      sx={{
                        py: 0.25, minHeight: 'auto',
                        '& .MuiListItemText-primary': { fontSize: '0.875rem' },
                        '& .MuiCheckbox-root': { py: 0.5 },
                      }}
                    >
                      <Checkbox checked={selectedCountries.includes(country)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={country} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                '& .MuiInputBase-input': { padding: '4px 8px' },
                '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
                '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
              }}
            >
              <InputLabel id="pt-retailer-select-label">Retailer</InputLabel>
              <Select
                labelId="pt-retailer-select-label"
                id="pt-retailer-select"
                value={selectedRetailers}
                label="Retailer"
                multiple
                onChange={(e) => setSelectedRetailers(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                input={<OutlinedInput label="Retailer" />}
                renderValue={(selected) => selected.join(', ')}
                sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 400, width: 280 } },
                  autoFocus: false,
                }}
                onClose={() => setRetailerSearchTerm('')}
              >
                <ListSubheader>
                  <TextField
                    size="small"
                    autoFocus
                    placeholder="Search retailers..."
                    fullWidth
                    value={retailerSearchTerm}
                    onChange={(e) => setRetailerSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </ListSubheader>
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const allRetailers = filteredRetailers;
                      if (selectedRetailers.length === allRetailers.length) {
                        setSelectedRetailers([]);
                      } else {
                        setSelectedRetailers(allRetailers);
                      }
                    }}
                  >
                    Select all
                  </Button>
                </Box>
                {(filteredRetailers ?? [])
                  .filter((r) => r.toLowerCase().includes(retailerSearchTerm.toLowerCase()))
                  .map((retailer) => (
                    <MenuItem
                      key={retailer}
                      value={retailer}
                      dense
                      sx={{
                        py: 0.25, minHeight: 'auto',
                        '& .MuiListItemText-primary': { fontSize: '0.875rem' },
                        '& .MuiCheckbox-root': { py: 0.5 },
                      }}
                    >
                      <Checkbox checked={selectedRetailers.includes(retailer)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={retailer} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>

          {/* Column 2: Brand + Product(s) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 280 }}>
            <Autocomplete
              multiple
              fullWidth
              options={columnsUniqueValues?.Brand ?? []}
              value={selectedBrands}
              onChange={handleBrandChange}
              disableCloseOnSelect
              renderTags={() => null}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option}>
                  <Checkbox checked={selected} size="small" sx={{ py: 0, mr: 1 }} />
                  <ListItemText primary={option} sx={{ my: 0 }} />
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Brand"
                  size="small"
                  placeholder="Search brands..."
                  InputLabelProps={{
                    sx: {
                      fontSize: '0.65rem',
                      transform: 'translate(14px, 8px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.85)',
                      },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {selectedBrands.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ mr: 1, whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.65rem' }}
                          >
                            {selectedBrands.length <= 2
                              ? selectedBrands.join(', ')
                              : `${selectedBrands.length} selected`}
                          </Typography>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                    '& .MuiInputBase-input': { padding: '4px 8px' },
                  }}
                />
              )}
            />

            <Tooltip title={selectedBrands.length === 0 ? 'Select Brand first' : ''} arrow>
              <Autocomplete
                multiple
                fullWidth
                disabled={false}
                options={columnsUniqueValues?.ReferenceProducts ?? []}
                value={selectedProducts}
                getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.title ?? opt.ean ?? '')}
                isOptionEqualToValue={(opt, val) =>
                  (typeof opt === 'string' ? opt : opt.ean) ===
                  (typeof val === 'string' ? val : val.ean)
                }
                onChange={(_, newValue) => setSelectedProducts(newValue)}
                disableCloseOnSelect
                renderTags={() => null}
                componentsProps={{ popper: { sx: { minWidth: 560 } } }}
                renderOption={(props, option, { selected }) => {
                  const label = typeof option === 'string' ? option : (option.title ?? option.ean ?? '');
                  const ean = typeof option === 'string' ? option : (option.ean ?? '');
                  const initials = label.replace(/^(ZYN|VELO|LYFT|ON!)\s*/i, '').slice(0, 2).toUpperCase();
                  const imgSrc = (typeof option === 'object' && option.image_url) ? option.image_url : undefined;
                  return (
                    <li {...props} key={ean || label} style={{ ...props.style, paddingTop: 3, paddingBottom: 3 }}>
                      <Checkbox checked={selected} size="small" sx={{ py: 0, mr: 0.5, flexShrink: 0 }} />
                      <Avatar
                        src={imgSrc}
                        sx={{
                          width: 30, height: 30, mr: 1, flexShrink: 0,
                          bgcolor: stringToColor(ean || label),
                          borderRadius: 1,
                          fontSize: '0.55rem',
                          fontWeight: 700,
                          letterSpacing: 0,
                        }}
                      >
                        {initials}
                      </Avatar>
                      <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.3 }}>
                        {label}
                      </Typography>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product(s)"
                    size="small"
                    placeholder="Search products..."
                    InputLabelProps={{
                      sx: {
                        fontSize: '0.65rem',
                        transform: 'translate(14px, 8px) scale(1)',
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -6px) scale(0.85)',
                        },
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {selectedProducts.length > 0 && (
                            <Typography
                              variant="caption"
                              sx={{ mr: 1, whiteSpace: 'nowrap', color: 'text.secondary' }}
                            >
                              {selectedProducts.length} selected
                            </Typography>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                      '& .MuiInputBase-input': { padding: '4px 8px' },
                    }}
                  />
                )}
              />
            </Tooltip>

          </Box>

          {!hideGroupByFilter && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 120 }}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                '& .MuiInputBase-input': { padding: '4px 8px' },
                '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
                '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
              }}
            >
              <InputLabel id="line-type-select-label">Group by</InputLabel>
              <Select
                labelId="line-type-select-label"
                id="line-type-select"
                value={lineType}
                label="Group by"
                onChange={(e) => setLineType(e.target.value)}
              >
                {lineTypeOptions.map((opt, i) => (
                  <MenuItem key={i} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          )}

          {/* Column 4: Product category + Pack size */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 140 }}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                '& .MuiInputBase-input': { padding: '4px 8px' },
                '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
                '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
              }}
            >
              <InputLabel id="product-category-label">Product category</InputLabel>
              <Select
                labelId="product-category-label"
                multiple
                value={selectedCategories}
                onChange={(e) => {
                  const newVal = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                  const clickedGroup = newVal.find((v) => typeof v === 'string' && v.startsWith('__group__'));
                  if (clickedGroup) {
                    handleCategoryGroupToggle(clickedGroup.replace('__group__', ''));
                  } else {
                    setSelectedCategories(newVal.filter((v) => !v.startsWith('__group__')));
                  }
                }}
                input={<OutlinedInput label="Product category" />}
                renderValue={categoryRenderValue}
                sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
              >
                {Object.entries(CATEGORY_GROUPS).map(([group, cats]) => {
                  const allSel = cats.every((c) => selectedCategories.includes(c));
                  const someSel = cats.some((c) => selectedCategories.includes(c));
                  return [
                    <MenuItem key={`__group__${group}`} value={`__group__${group}`}>
                      <Checkbox checked={allSel} indeterminate={!allSel && someSel} />
                      <ListItemText primary={<strong>{group}</strong>} />
                    </MenuItem>,
                    ...cats.map((cat) => (
                      <MenuItem key={cat} value={cat} sx={{ pl: 4 }}>
                        <Checkbox checked={selectedCategories.includes(cat)} />
                        <ListItemText primary={cat} />
                      </MenuItem>
                    )),
                  ];
                })}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                '& .MuiInputBase-input': { padding: '4px 8px' },
                '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
                '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
              }}
            >
              <InputLabel id="pack-size-select-label">Pack size</InputLabel>
              <Select
                labelId="pack-size-select-label"
                id="pack-size-select"
                value={selectedPackSizes}
                label="Pack size"
                multiple
                onChange={(e) => setSelectedPackSizes(typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : e.target.value)}
                input={<OutlinedInput label="Pack size" />}
                renderValue={(selected) =>
                  [...selected].sort((a, b) => a - b).map(n => `${n}-pack`).join(', ')
                }
                sx={{ '& .MuiSelect-select': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
                MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
              >
                {(columnsUniqueValues?.['Units (#)'] ?? [])
                  .map(Number)
                  .filter(n => !isNaN(n) && n > 0)
                  .sort((a, b) => a - b)
                  .map((size) => (
                    <MenuItem
                      key={size}
                      value={size}
                      dense
                      sx={{
                        py: 0.25, minHeight: 'auto',
                        '& .MuiListItemText-primary': { fontSize: '0.875rem' },
                        '& .MuiCheckbox-root': { py: 0.5 },
                      }}
                    >
                      <Checkbox checked={selectedPackSizes.includes(size)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={`${size}-pack`} sx={{ my: 0 }} />
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>

          {/* Column 5: Currency + Price type */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 47 }}>
            <Tooltip title={isCurrencyLocked ? `Auto-set based on selected country` : ''} arrow>
              <FormControl
                fullWidth
                size="small"
                disabled={isCurrencyLocked}
                sx={{
                  '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                  '& .MuiInputBase-input': { padding: '4px 8px' },
                  '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
                  '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
                }}
              >
                <InputLabel id="currency-simple-select-label">Currency</InputLabel>
                <Select
                  labelId="currency-simple-select-label"
                  id="currency-simple-select"
                  value={selectedCurrency ?? ''}
                  label="Currency"
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                >
                  {columnsUniqueValues?.Currency?.map((currency, index) => (
                    <MenuItem key={index} value={currency}>{currency}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>

            <FormControl
              fullWidth
              size="small"
              disabled
              sx={{
                '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                '& .MuiInputBase-input': { padding: '4px 8px' },
                '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
                '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
              }}
            >
              <InputLabel id="price-type-select-label">Price type</InputLabel>
              <Select
                labelId="price-type-select-label"
                id="price-type-select"
                value={inSaleStatus}
                label="Price type"
                onChange={(e) => setInSaleStatus(e.target.value)}
              >
                {priceTypeOptions.map((opt, i) => (
                  <MenuItem key={i} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Column 6: Start date + End date + Slider */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                <DatePicker
                  label="Start"
                  value={startDate}
                  onChange={handleStartDateChange}
                  format="DD/MM/YY"
                  minDate={dayjs('2025-01-01')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      InputLabelProps: { sx: { fontSize: '0.65rem' } },
                      sx: {
                        width: 112,
                        '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                        '& .MuiInputBase-input': { padding: '4px 4px 4px 8px' },
                        '& .MuiInputAdornment-root': { ml: 0 },
                      },
                    },
                  }}
                />
                <DatePicker
                  label="End"
                  value={endDate}
                  onChange={handleEndDateChange}
                  format="DD/MM/YY"
                  minDate={dayjs('2025-01-01')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      InputLabelProps: { sx: { fontSize: '0.65rem' } },
                      sx: {
                        width: 112,
                        '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                        '& .MuiInputBase-input': { padding: '4px 4px 4px 8px' },
                        '& .MuiInputAdornment-root': { ml: 0 },
                      },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleDataRefresh}
                  disabled={isDbRefreshing}
                  startIcon={isDbRefreshing ? <CircularProgress size={12} color="inherit" /> : <Refresh sx={{ fontSize: '1rem' }} />}
                  sx={{
                    backgroundColor: accent.main,
                    '&:hover': { backgroundColor: accent.hover },
                    fontWeight: 500,
                    fontSize: '0.5rem',
                    whiteSpace: 'nowrap',
                    height: 30,
                    width: 112,
                    px: 1,
                    py: 0,
                    minWidth: 112,
                  }}
                >
                  {isDbRefreshing ? 'Refreshing...' : 'Data Refresh'}
                </Button>
              </Box>
              <Box sx={{ width: 'calc(112px + 6px + 112px)', px: 0.5 }}>
                <Slider
                  value={Array.isArray(dateSliderValue) ? dateSliderValue : [dateSliderValue, dateSliderValue]}
                  onChange={handleDateSliderChange}
                  onChangeCommitted={handleDateSliderCommit}
                  min={0}
                  max={Math.max(availableDates.length - 1, 0)}
                  step={1}
                  disableSwap
                  valueLabelDisplay="off"
                  sx={{
                    color: accent.main,
                    '& .MuiSlider-track': { backgroundColor: accent.main },
                    '& .MuiSlider-rail': { backgroundColor: accent.rail },
                    '& .MuiSlider-thumb': { borderColor: accent.main },
                  }}
                />
              </Box>
            </Box>
          </LocalizationProvider>
        </Box>
      </Paper>
      </ThemeProvider>

      <div style={{
        display: 'flex',
        gap: '0.5rem',
        margin: '1rem 0'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          flex: '1 1 auto',
          minWidth: 0,
          maxWidth: 'calc(100% - 230px)'
        }}>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {priceMonitoringChartTitle}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {showChart && (
                  <>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                      Show Top:
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setTopN((prev) => Math.max(1, prev - 1))}
                      disabled={topN <= 1}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <TextField
                      value={topN}
                      size="small"
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        if (!isNaN(value) && value >= 1 && value <= 50) {
                          setTopN(value);
                        }
                      }}
                      inputProps={{
                        type: 'number',
                        min: 1,
                        max: 50,
                        style: { textAlign: 'center', width: '50px' }
                      }}
                      sx={{ width: '60px' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setTopN((prev) => Math.min(50, prev + 1))}
                      disabled={topN >= 50}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap', color: 'text.secondary', ml: 0.5 }}>
                      Showing {Math.min(topN, totalDatasetsCount)}/{totalDatasetsCount} lines
                    </Typography>
                  </>
                )}
                <Tooltip title="Export CSV" arrow>
                  <span>
                    <IconButton size="small" onClick={handleExportCSV} disabled={!topNDatasets.length}>
                      <FileDownload fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Chart view" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setShowChart(true)}
                    sx={{ color: showChart ? 'primary.main' : 'text.disabled' }}
                  >
                    <ShowChart fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Table view" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setShowChart(false)}
                    sx={{ color: !showChart ? 'primary.main' : 'text.disabled' }}
                  >
                    <TableRows fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {showChart ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  sx={{
                    resize: 'vertical',
                    overflow: 'hidden',
                    minHeight: 200,
                    height: 528,
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid transparent',
                    borderBottomColor: 'divider',
                    pb: '6px',
                  }}
                >
                  <Box sx={{ position: 'relative', flex: 1, minHeight: 0, minWidth: 0 }}>
                    {chartData ? (
                      <Line
                        key="product_view_line_chart"
                        options={lineChartOptions}
                        data={chartData}
                      />
                    ) : (
                      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Loading...
                      </Box>
                    )}
                    {isRefreshing && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255,255,255,0.6)',
                          zIndex: 1,
                          pointerEvents: 'none',
                        }}
                      >
                        <CircularProgress size={28} />
                      </Box>
                    )}
                  </Box>
                </Box>
                {topNDatasets.length > 0 && (
                  <MonitoringChartLegend
                    datasets={topNDatasets}
                    visibleDatasets={visibleDatasets}
                    onToggleDataset={handleToggleDataset}
                    onShowAll={handleShowAll}
                    onHideAll={handleHideAll}
                    searchTerm={legendSearchTerm}
                    onSearchChange={setLegendSearchTerm}
                    missingDataMode={missingDataMode}
                    onMissingDataModeChange={setMissingDataMode}
                    legendExpanded={legendExpanded}
                    onLegendExpandToggle={() => setLegendExpanded((v) => !v)}
                    onAxisSettingsOpen={(el) => setAxisAnchorEl(el)}
                    axisSettingsActive={axisSettingsActive}
                  />
                )}
                <Popover
                  open={Boolean(axisAnchorEl)}
                  anchorEl={axisAnchorEl}
                  onClose={() => setAxisAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, width: 240 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.04em' }}>
                      Y-Axis Settings
                    </Typography>
                    <TextField
                      label="Min value"
                      size="small"
                      type="number"
                      value={yMin}
                      onChange={(e) => setYMin(e.target.value)}
                      placeholder="Auto"
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                    />
                    <TextField
                      label="Max value"
                      size="small"
                      type="number"
                      value={yMax}
                      onChange={(e) => setYMax(e.target.value)}
                      placeholder="Auto"
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                    />
                    <TextField
                      label="Tick step size"
                      size="small"
                      type="number"
                      value={yStepSize}
                      onChange={(e) => setYStepSize(e.target.value)}
                      placeholder="Auto"
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showChartLabels}
                          onChange={(e) => setShowChartLabels(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Show chart labels"
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                    />
                    <Divider />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setYMin('');
                        setYMax('');
                        setYStepSize('');
                        setShowChartLabels(true);
                      }}
                      sx={{ fontSize: '0.7rem', py: 0.25 }}
                    >
                      Reset all to auto
                    </Button>
                  </Box>
                </Popover>
              </Box>
            ) : (
              <Box sx={{ height: 528 }}>
                {chartTableRows.length > 0 ? (
                  <DataGrid
                    rows={chartTableRows}
                    columns={chartTableColumns}
                    rowHeight={30}
                    density="compact"
                    initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                    pageSizeOptions={[25, 50, 100]}
                    disableSelectionOnClick
                    sx={{ height: '100%' }}
                  />
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="caption" color="text.secondary">No data</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <div>
            {gridData ? (
              <DataGrid
                rowHeight={35}
                rows={gridData.rows}
                columns={columnsWithClickableUrls.map(column => ({
                  ...column,
                  resizable: true,
                  headerAlign: 'center',
                }))}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 100,
                    },
                  },
                }}
                pageSizeOptions={[25, 50, 100]}
                disableSelectionOnClick
                density='compact'
                sx={{
                  height: '350px',
                  overflowX: 'scroll'
                }}
              />
            ) : (
              <div>Loading...</div>
            )}
          </div>
        </div>

        <div style={{
          width: '160px',
          flexShrink: 0
        }}>
          {KpiElements(kpiData, { size: 'sm' })}
        </div>
      </div>

      {/* Full Dataset Panel */}
      <Box sx={{ mt: 2, border: '1px solid #e0e0e0', borderRadius: 1, display: 'none' }}>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 1.5, py: 0.75, cursor: 'pointer', backgroundColor: '#f5f5f5',
            borderRadius: showFullDataset ? '4px 4px 0 0' : 1,
            '&:hover': { backgroundColor: '#ebebeb' },
          }}
          onClick={handleToggleFullDataset}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableRows sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Full Dataset
            </Typography>
            {rawData && (
              <Typography variant="caption" color="text.secondary">
                ({rawData.total_rows.toLocaleString()} rows)
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showFullDataset && rawData && (
              <Tooltip title="Reload with current filters" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setRawData(null); setRawDataPage(0); loadFullDataset(0, rawDataPageSize); }}
                  disabled={rawDataLoading}
                >
                  <Refresh sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            )}
            {showFullDataset ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </Box>
        </Box>

        {showFullDataset && (
          <Box sx={{ p: 1 }}>
            {rawDataLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : rawData ? (
              <>
                <DataGrid
                  rows={rawData.rows}
                  columns={rawData.columns}
                  rowHeight={30}
                  density="compact"
                  sx={{ height: 420, overflowX: 'scroll' }}
                  disableSelectionOnClick
                  hideFooter
                />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Rows per page:
                  </Typography>
                  <Select
                    size="small"
                    value={rawDataPageSize}
                    onChange={(e) => {
                      const newSize = e.target.value;
                      setRawDataPageSize(newSize);
                      setRawDataPage(0);
                      loadFullDataset(0, newSize);
                    }}
                    sx={{ height: 24, fontSize: '0.75rem', '& .MuiSelect-select': { py: 0, px: 1 } }}
                  >
                    {[50, 100, 250, 500].map(n => <MenuItem key={n} value={n} sx={{ fontSize: '0.75rem' }}>{n}</MenuItem>)}
                  </Select>
                  <Typography variant="caption" color="text.secondary">
                    {rawDataPage * rawDataPageSize + 1}–{Math.min((rawDataPage + 1) * rawDataPageSize, rawData.total_rows)} of {rawData.total_rows.toLocaleString()}
                  </Typography>
                  <Tooltip title="Previous page" arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled={rawDataPage === 0 || rawDataLoading}
                        onClick={() => { const p = rawDataPage - 1; setRawDataPage(p); loadFullDataset(p, rawDataPageSize); }}
                      >
                        <Remove sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Next page" arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled={(rawDataPage + 1) * rawDataPageSize >= rawData.total_rows || rawDataLoading}
                        onClick={() => { const p = rawDataPage + 1; setRawDataPage(p); loadFullDataset(p, rawDataPageSize); }}
                      >
                        <Add sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">No data loaded.</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      </div>
    </div>
  );
}

export default BatProductView;
